import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import type { CourseInput, LessonInput } from './types';

export const courseKeys = {
  all: ['courses-lms'] as const,
  lists: () => [...courseKeys.all, 'list'] as const,
  list: (params: api.CourseListParams) => [...courseKeys.lists(), params] as const,
  detail: (id: string | number) => [...courseKeys.all, 'detail', id] as const,
  lessons: (id: number) => [...courseKeys.all, 'lessons', id] as const,
  enrollments: () => [...courseKeys.all, 'enrollments'] as const,
};

/** Truthy id that isn't the `-1` "skip" sentinel used by optional lookups. */
function isResolvable(id: string | number): boolean {
  return id !== '' && id !== -1 && id !== '-1' && id != null;
}

export function useCourses(params: api.CourseListParams) {
  return useQuery({
    queryKey: courseKeys.list(params),
    queryFn: () => api.listCourses(params),
    placeholderData: keepPreviousData,
  });
}

export function useCourse(id: string | number) {
  return useQuery({
    queryKey: courseKeys.detail(id),
    queryFn: () => api.getCourse(id),
    // Resolves by public_id (string, from the URL) or integer pk; the backend
    // accepts either (ADR-041).
    enabled: isResolvable(id),
  });
}

export function useLessons(courseId: number) {
  return useQuery({
    queryKey: courseKeys.lessons(courseId),
    queryFn: () => api.listLessons(courseId),
    enabled: Number.isInteger(courseId) && courseId > 0,
  });
}

export function useMyEnrollments() {
  return useQuery({
    queryKey: courseKeys.enrollments(),
    queryFn: () => api.listMyEnrollments(),
  });
}

export function useCreateCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CourseInput) => api.createCourse(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: courseKeys.lists() });
      toast.success('Course created');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateCourse(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<CourseInput>) => api.updateCourse(id, input),
    onSuccess: (course) => {
      qc.invalidateQueries({ queryKey: courseKeys.lists() });
      qc.setQueryData(courseKeys.detail(id), course);
      toast.success('Course updated');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useDeleteCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteCourse(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: courseKeys.lists() });
      toast.success('Course deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useCreateLesson(courseId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<LessonInput, 'course'>) =>
      api.createLesson({ ...input, course: courseId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: courseKeys.lessons(courseId) });
      toast.success('Lesson added');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useEnroll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (courseId: number) => api.enroll(courseId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: courseKeys.enrollments() });
      toast.success('Enrolled');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useCompleteLesson(enrollmentId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lessonId: number) => api.completeLesson(enrollmentId, lessonId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: courseKeys.enrollments() });
      toast.success('Lesson completed');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}
